import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import moment from 'moment-timezone';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Button from 'WeployDesignLanguage/buttons/Button';
import {
  Face,
  Calendar,
  Location,
  Tshirt,
  PersonAdd,
  AccessTime,
  Remote,
} from 'WeployDesignLanguage/icons';
import ExpansionPanel from './ExpansionPanel';

import { longDate } from '../../../../utils/Date';

const summaryColumnWidths = css`
  & > div:nth-child(1) { width: 25%; }
  & > div:nth-child(2) { width: 30%; }
  & > div:nth-child(3) { width: 20%; }
  & > div:nth-child(4) { width: 25%; }
`;

const StyledExpansionSummaryContent = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 100%;
  margin: ${props => props.theme.spacing[2]} 0;

  ${summaryColumnWidths};

  & > *:not(:last-child) {
    margin-right: ${props => props.theme.spacing[3]};
  }
`;

const StyledJobTitleColumn = styled.div``;

const StyledPanelHeader = styled.div`
  display: none;
`;

const StyledExpansionPanel = styled(ExpansionPanel)`
  color: ${props => props.theme.colors.text.primary};
`;

const expansionPanelStyles = css`
  &.Mui-expanded {
    ${StyledJobTitleColumn} {
      display: none;
    }

    ${StyledPanelHeader} {
      display: block;
      min-width: 100%;
    }

    ${StyledExpansionSummaryContent} {
      display: none;
    }
  }
`;

const StyledList = styled.ul`
  padding: 0;
  padding-left: ${props => props.theme.spacing[3]};
  margin: 0 0 ${props => props.theme.spacing[2]};
`;

const StyledListItem = styled.li`
  &:not(:last-child) {
    margin-bottom: ${props => props.theme.spacing[1]};
  }
`;

const StyledIconList = styled.ul`
  padding: 0;
  list-style-type: none;
  margin: 0 0 ${props => props.theme.spacing[2]};
`;

const StyledIconListItem = styled.li`
  display: flex;

  &:not(:last-child) {
    margin-bottom: ${props => props.theme.spacing[1]};
  }

  & > svg {
    min-width: ${props => props.theme.icons.default};
    color: ${props => props.theme.colors.text.primary};
    margin-right: ${props => props.theme.spacing[1]};
  }
`;

const StyledParagraph = styled.p`
  margin-top: 0;
`;

const StyledDetailsColumn = styled.div`
  flex: 1;

  &:not(:last-child) {
    margin-right: ${props => props.theme.spacing[3]};
  }
`;

const StyledColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledPlaceholderText = styled.span`
  color: ${props => props.theme.colors.text.secondary};
`;

const StyledExpansionPanelActions = styled(ExpansionPanel.Actions)`
  padding: ${props => props.theme.spacing[3]};
  padding-top: ${props => props.theme.spacing[1]};
`;

const renderTasks = tasks => tasks.map(task => (
  <StyledListItem key={task}>
    {task}
  </StyledListItem>
));

const PastJobRow = ({ job, onChooseClick }) => {
  const {
    belongsToCurrentUser,
    createdAt,
    dressCode,
    experience,
    finishTime,
    id,
    isRemote,
    latestPositionFinishTime,
    location: {
      street,
      city,
      state,
      postcode,
    },
    meetingInstructions,
    numberOfPositions,
    owner,
    startTime,
    tasks,
    timeRequirements,
    title,
  } = job;

  const calculatedFinishTime = latestPositionFinishTime || finishTime;
  const jobOwner = belongsToCurrentUser ? 'me' : owner;
  const postedDate = longDate(createdAt);

  return (
    <StyledExpansionPanel
      expansionPanelStyles={expansionPanelStyles}
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
    >
      <ExpansionPanel.Summary expandIcon={<ExpandMoreIcon />}>
        <StyledPanelHeader>
          <h5>{title}</h5>
        </StyledPanelHeader>
        <StyledExpansionSummaryContent>
          <StyledJobTitleColumn>
            <strong>{title}</strong>
          </StyledJobTitleColumn>
          <div>{`${street}, ${city} ${state} ${postcode}`}</div>
          <StyledColumn>
            <div>Posted on</div>
            &nbsp;
            <div>{postedDate}</div>
          </StyledColumn>
          <StyledColumn>
            <div>Owned by</div>
            &nbsp;
            <div>{jobOwner}</div>
          </StyledColumn>
        </StyledExpansionSummaryContent>
      </ExpansionPanel.Summary>
      <ExpansionPanel.Details>
        <StyledDetailsColumn>
          <StyledIconList noBullets>
            <StyledIconListItem><AccessTime /> Posted on: {postedDate}<br /></StyledIconListItem>
            <StyledIconListItem><Face /> Owned by: {jobOwner}<br /></StyledIconListItem>
            <StyledIconListItem><PersonAdd /> Required Weployees: {numberOfPositions}<br /></StyledIconListItem>
            <StyledIconListItem><Location /> Location: {`${street}, ${city} ${state} ${postcode}`}<br /></StyledIconListItem>
            <StyledIconListItem><Calendar /> {moment(startTime).format('MMM YYYY')} to {moment(calculatedFinishTime).format('MMM YYYY')}<br /></StyledIconListItem>
            <StyledIconListItem><Tshirt /> Dress code: {dressCode}</StyledIconListItem>
            <StyledIconListItem><Remote /> Remote: {isRemote ? 'Yes' : 'No'}</StyledIconListItem>
          </StyledIconList>
          <h6>Meeting instructions</h6>
          <StyledParagraph>{meetingInstructions || <StyledPlaceholderText>No meeting instructions were specified</StyledPlaceholderText> }</StyledParagraph>
          <h6>Time requirements</h6>
          <StyledParagraph>{timeRequirements || <StyledPlaceholderText>No time requirements were specified</StyledPlaceholderText>}</StyledParagraph>
        </StyledDetailsColumn>
        <StyledDetailsColumn>
          <h6>Main tasks</h6>
          <StyledList>
            { renderTasks(tasks) }
          </StyledList>
        </StyledDetailsColumn>
        <StyledDetailsColumn>
          <h6>Systems experience</h6>
          <StyledParagraph>{experience || <StyledPlaceholderText>No system experience was specified</StyledPlaceholderText>}</StyledParagraph>
        </StyledDetailsColumn>
      </ExpansionPanel.Details>
      <StyledExpansionPanelActions>
        <Button onClick={() => onChooseClick(id)}>Choose and review</Button>
      </StyledExpansionPanelActions>
    </StyledExpansionPanel>
  );
};

PastJobRow.propTypes = {
  job: PropTypes.object.isRequired,
  onChooseClick: PropTypes.func.isRequired,
};

export default PastJobRow;
